import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { fetchContentFromFirebase } from '../services/contentService';

export const useContentViewer = () => {
  const { id } = useParams();
  const { darkMode, setDarkMode } = useTheme();
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [readSections, setReadSections] = useState(() => {
    const saved = localStorage.getItem(`read-sections-${id}`);
    return saved ? JSON.parse(saved) : [];
  });

  const calculateProgress = () => {
    if (!sections.length) return 0;
    return Math.round((readSections.length / sections.length) * 100);
  };

  const markSectionAsRead = (index) => {
    if (!readSections.includes(index)) {
      const newReadSections = [...readSections, index].sort((a, b) => a - b);
      setReadSections(newReadSections);
      localStorage.setItem(`read-sections-${id}`, JSON.stringify(newReadSections));
    }
  };

  const resetProgress = () => {
    if (window.confirm('Are you sure you want to reset all progress?')) {
      setReadSections([]);
      localStorage.removeItem(`read-sections-${id}`);
      setCurrentSection(0);
    }
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      markSectionAsRead(currentSection);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleMarkComplete = () => {
    toggleSectionCompletion(currentSection);
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const toggleSectionCompletion = (index) => {
    const newReadSections = readSections.includes(index)
      ? readSections.filter(i => i !== index)
      : [...readSections, index].sort((a, b) => a - b);
    
    setReadSections(newReadSections);
    localStorage.setItem(`read-sections-${id}`, JSON.stringify(newReadSections));
  };

  const handleContinue = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  useEffect(() => {
    const loadContent = async () => {
      const content = await fetchContentFromFirebase(id);
      setSections(content);
    };
    loadContent();
  }, [id]);

  return {
    sections,
    currentSection,
    darkMode,
    setDarkMode,
    readSections,
    setCurrentSection,
    calculateProgress,
    handleMarkComplete,
    goToPreviousSection,
    resetProgress,
    toggleSectionCompletion,
    handleContinue,
  };
};
